




































































































































































































import {
  Vue,
  Component,
  Prop,
  Watch,
  Inject
} from "@/shared/vuepack";
import { Validator } from "vee-validate";
import { Employee, CensusManager } from "@/employer/types";
import {
  FetchState, FundingOption,
  SortOrder, EmployeeListSortOption, Permission
} from "@/shared/types";
import moment from "moment";
import { Location } from "vue-router";
import ActionMenu from "@/shared/components/ui/ActionMenu.vue";
import Button from "@/shared/components/ui/Button.vue";
import EnrollmentStatus from "@/employer/components/shared/EnrollmentStatus.vue";
import Pagination from "@/shared/components/ui/Pagination.vue";
import PageSizeSelectList from "@/shared/components/ui/PageSizeSelectList.vue";
import EnrollmentWidget from "@/employer/components/widgets/EnrollmentWidget.vue";
import EmployeesNavigation from "@/employer/components/user-management/EmployeesNavigation.vue";
import EmployeeNavigatorWidget from "@/employer/components/widgets/EmployeeNavigatorWidget.vue";
import AddEmployeeLinkWidget from "@/employer/components/widgets/AddEmployeeLinkWidget.vue";
import { useNotifierStore } from "@/shared/store/notifier";
import { useEmployeeStore } from "@/employer/store/employee";
import { useUserStore } from "@/shared/store/user";
import { useRolePermissionsStore } from "@/shared/store/rolePermissions";

const initialSearchTerms = {
  firstName: "",
  lastName: "",
  employeeId: ""
};

@Component({
  components: {
    Button,
    EnrollmentStatus,
    ActionMenu,
    Pagination,
    PageSizeSelectList,
    EnrollmentWidget,
    EmployeesNavigation,
    EmployeeNavigatorWidget,
    AddEmployeeLinkWidget
  }
})
export default class EmployeeList extends Vue {
  @Inject()
  $validator: Validator;

  employees: Employee[] = [];
  notifierStore = useNotifierStore();
  employeeStore = useEmployeeStore();
  userStore = useUserStore();
  rolePermissionsStore = useRolePermissionsStore();

  EmployeeListSortOption = EmployeeListSortOption;
  actionMenuIndexDisplay = null;
  isSubmitting = false;
  searchTerm = { ...initialSearchTerms };
  searchResultCount = 0;
  selectedEmployee = null;

  @Prop()
  page: number;

  @Prop()
  routeToEmployeeList: (page: number) => Location;

  @Prop()
  routeToEmployeeEdit: (employeeId: number) => Location;

  @Prop()
  routeToCensusUpload;

  @Prop()
  routeToManagePayroll;

  mounted (): void {
    this.searchTerm = { ...initialSearchTerms };
    this.employeeStore.sortOption = this.employeeStore.defaultSortOption;
    this.loadEmployees(false, this.employeeStore.sortOption);
  }

  get displayEmployeeNavigatorContent (): boolean {
    return this.userStore.currentEmployer && this.userStore.currentEmployer.censusManager === CensusManager.employeeNavigator;
  }

  get isEmployerTerminated (): boolean {
    return this.userStore.currentEmployer && this.userStore.currentEmployer.isTerminated;
  }

  get sortedAscending (): boolean {
    return this.employeeStore.sortOrder === SortOrder.Ascending;
  }

  get employeeIdSorted (): boolean {
    return this.employeeStore.sortOption === EmployeeListSortOption.ExternalEmployeeId;
  }

  get nameSorted (): boolean {
    return this.employeeStore.sortOption === EmployeeListSortOption.Name;
  }

  get planTypeSorted (): boolean {
    return this.employeeStore.sortOption === EmployeeListSortOption.PlanType;
  }

  get hsaStartDateSorted (): boolean {
    return this.employeeStore.sortOption === EmployeeListSortOption.HsaStartDate;
  }

  get employmentStatusSorted (): boolean {
    return this.employeeStore.sortOption === EmployeeListSortOption.EmploymentStatus;
  }

  get enrollmentStatusSorted (): boolean {
    return this.employeeStore.sortOption === EmployeeListSortOption.EnrollmentStatus;
  }

  get hasEmployeeWritePermission (): boolean {
    return this.rolePermissionsStore.userHasPermission(Permission.EmployeeWrite);
  }

  get isFormValid () {
    return !this.$validator.errors.any();
  }

  get isSearchDisabled () {
    return this.isSubmitting === true || !this.isFormValid;
  }

  get isClearDisabled () {
    return this.isSubmitting === true;
  }

  onSearch (): void {
    if (this.isSubmitting) return;
    this.$validator
      .validate()
      .then(result => {
        if (!result) return;
        this.isSubmitting = true;
        this.employeeStore.setPageNumber(1);
        this.$router.push(this.routeToEmployeeList(1));
        this.loadEmployees(true, this.employeeStore.sortOption);
      }).finally(() => this.isSubmitting = false);
  }

  clearSearch (): void {
    if (!this.isSubmitting) {
      this.isSubmitting = true;
      this.searchTerm = { ...initialSearchTerms };
      this.employeeStore.setPageNumber(1);
      this.employeeStore.setSortOption(EmployeeListSortOption.None, true);
      this.employeeStore.sortOption = this.employeeStore.defaultSortOption;
      this.$router.push(this.routeToEmployeeList(1));
      this.loadEmployees(true, this.employeeStore.sortOption);
      this.isSubmitting = false;
    }
  }

  selectSortOption (sortOption: EmployeeListSortOption): void {
    // on switching the sorting, reset to page 1
    this.employeeStore.setPageNumber(1);
    this.employeeStore.setSortOption(sortOption);
    this.$router.push(this.routeToEmployeeList(1));
    this.loadEmployees(true, sortOption);
  }

  switchPageSize (): void {
    // on switching page size, reset to page 1
    this.employeeStore.setPageNumber(1);
    this.$router.push(this.routeToEmployeeList(1));
    this.loadEmployees(true, this.employeeStore.sortOption);
  }

  @Watch("page")
  onRouteValuePageNumberChanged (): void {
    this.actionMenuIndexDisplay = null;
    this.loadEmployees(false, this.employeeStore.sortOption);
  }

  redirectToEmployeeEdit (employeeId: number): void {
    this.$router.push(this.routeToEmployeeEdit(employeeId));
  }

  get isLoaded (): boolean {
    return this.employeeStore.getFetchState === FetchState.Loaded;
  }

  toggleActionMenu (index): void {
    this.actionMenuIndexDisplay = index === this.actionMenuIndexDisplay ? null : index;
  }

  goToEmployeeEditNew (): void {
    this.$router.push(this.routeToEmployeeEdit(null));
  }

  goToCensusUpload (): void {
    this.$router.push(this.routeToCensusUpload);
  }

  get displayReportLink (): boolean {
    if (this.userStore.hasAdminAccess) {
      return true;
    }

    return this.userStore.currentEmployer.fundingOption === FundingOption.ErCalculateErPush;
  }

  get displayManageEmployeesLink (): boolean {
    if (!this.rolePermissionsStore.userHasPermission(Permission.PayrollRead)) {
      return false;
    }

    return this.userStore.currentEmployer.fundingOption === FundingOption.BendCalculatePull;
  }

  enrollmentStatusDisplayText (employee): string {
    return this.employeeStore.enrollmentStatusText(employee);
  }

  employmentStatusDisplayText (employee): string {
    return this.employeeStore.employmentStatusText(employee);
  }

  displayInitials (employee): string {
    if (!employee.firstName || !employee.lastName) {
      return "NA";
    }
    return `${employee.firstName[0]}${employee.lastName[0]}`;
  }

  accountTypeDisplayText (accountType): string {
    if (accountType === "individual") {
      return "Individual";
    }
    return "Family";
  }

  eligibilityDateFormated (eligibilityDate): string {
    return moment(eligibilityDate).format("MM/DD/YYYY");
  }

  sendInvite (employee): void {
    const request = {
      employerId: this.userStore.employerId,
      employeeId: employee.employeeId
    };
    this.employeeStore.resendEmployeeInviteLink(request)
      .then(() => this.notifierStore.setSuccess(`Invite sent to ${employee.firstName} ${employee.lastName}`))
      .catch(this.handleError);
  }

  handleError (err: any): void {
    this.notifierStore.setDanger(
      (err && err.errorMessage) ||
      "Error processing your request. Please try again later"
    );
  }

  switchPage (toPage): void {
    this.$router.push(this.routeToEmployeeList(toPage));
  }

  loadEmployees (clearState: boolean, sortOption: EmployeeListSortOption): void {
    if (!clearState) {
      this.employeeStore.setPageNumber(this.page);
    }
    this.employeeStore.getEmployeesList(
      this.userStore.employerId,
      this.employeeStore.pageNumber,
      clearState,
      {
        employeeId: this.searchTerm.employeeId,
        firstname: this.searchTerm.firstName,
        lastname: this.searchTerm.lastName
      }
    )
      .then(() => {
        this.employees = this.employeeStore.getEmployeesByPageNumber(this.employeeStore.pageNumber);
      })
      .catch(this.handleError);
  }

  get displayOldCensusUploadLink (): boolean {
    return false;
  }
}
